import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InspectorService } from '../../inspector/inspector.service';
import { Inspection, Inspector } from '../../shared/interfaces';
import { InspectionService } from '../inspection.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-assign-inspector-inspection',
  templateUrl: './assign.inspector.component.html',
  styleUrls: ['./assign.inspector.component.scss'],
})
export class InspectionAssignInspectorComponent implements OnInit {
  @ViewChild('assignInspectorModal', { static: true }) public assignInspectorModal: ModalDirective;

  confirmCallback: () => void;
  cancelCallback: () => void;
  inspection: Inspection;

  assignInspectorForm: UntypedFormGroup;
  inspectorDataSource: any[] = [];
  selectedInspector: any;
  saving = false;
  isInspectorsLoaded = false;
  viewMoreClicked: boolean = false;
  inspectionProductTypeName: string;
  allInspectors: any[] = [];
  vendorInspectors: any[] = [];
  inspectorMismatch: boolean = false;

  constructor(
    public inspectorService: InspectorService,
    public inspectionService: InspectionService,
    private fb: UntypedFormBuilder,
    private toast: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.assignInspectorForm = this.fb.group({ inspector: [{ value: '', disabled: true }] });

    this.assignInspectorModal.onHidden.subscribe(x => {
      if (x.dismissReason === 'esc' || x.dismissReason === 'backdrop-click') {
        this.cancel();
        this.viewMoreClicked = false;
      }
    });
  }

  loadAllInspectors() {
    this.inspectorService
      .inspectorsWithMiles({
        search: '',
        distanceTo: [this.inspection.location[0], this.inspection.location[1]].toString(),
      })
      .pipe()
      .subscribe(
        inspectors => {
          this.inspectorDataSource = Array.isArray(inspectors) ? [...inspectors] : [{}];
          this.isInspectorsLoaded = true;
          this.cdr.markForCheck();
        },
        error => {
          console.error('Error fetching inspectors:', error);
          this.inspectorDataSource = [];
        },
      );
  }

  onInputClick() {
    if (!this.isInspectorsLoaded) {
      this.loadAllInspectors();
    }
  }

  userBlurInspector(ev: FocusEvent) {
    if (!this.selectedInspector) {
      this.assignInspectorForm.controls.inspector.setValue('');
    }
  }

  show(options: { confirmed?: () => void; canceled?: () => void; inspection: Inspection }) {
    this.confirmCallback = options.confirmed;
    this.cancelCallback = options.canceled;
    this.inspection = options.inspection;

    this.inspectionProductTypeName = options?.inspection?.policy?.productType?.name;
    this.selectedInspector = this.inspection.inspector;
    this.assignInspectorForm.controls.inspector.setValue(this.selectedInspector ? this.selectedInspector.name : '');
    this.loadVendorInspectors();
    this.assignInspectorModal.show();
    this.assignInspectorForm.get('inspector').enable();
  }

  async save() {
    if (this.saving) return;
    if (this.assignInspectorForm.valid) {
      this.saving = true;
      if (this.selectedInspector) {
        await this.inspectionService.assignInspector(this.inspection.id, this.selectedInspector);
        this.toast.success('Inspection saved!');
      } else {
        this.toast.warning('Please assign Inspector!');
      }
      this.viewMoreClicked = false;
      this.assignInspectorModal.hide();
      this.saving = false;
      return this.confirmCallback && this.confirmCallback();
    }
  }

  cancel() {
    this.assignInspectorModal.hide();
    this.viewMoreClicked = false;
    return !!this.cancelCallback && this.cancelCallback();
  }

  onInspectorSelect(inspector: any) {
    this.inspectorMismatch = false;
    if (!this.inspectorHasMatchingProductType(inspector)) {
      this.selectedInspector = inspector;
      this.selectInspector(inspector);
      this.inspectorMismatch = true;
      this.viewMoreClicked = true;
    } else {
      this.selectInspector(inspector);
    }
  }

  inspectorHasMatchingProductType(inspector: any): boolean {
    if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
      return false;
    }
    const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
    return productTypesArray.includes(this.inspectionProductTypeName);
  }

  selectInspector(inspector: any) {
    this.selectedInspector = inspector;

    this.assignInspectorForm.get('inspector').setValue({
      id: inspector.id,
      name: inspector.name,
      email: inspector.email,
      phone: inspector.phone,
    });
    this.cdr.detectChanges();
  }

  inspectorTrackByFn(inspector: Inspector) {
    return inspector.id;
  }

  getValidationClass(field: string) {
    const control = this.assignInspectorForm.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  viewMoreInspectors() {
    this.viewMoreClicked = true;
    this.loadAllInspectors();
  }

  async loadVendorInspectors() {
    this.inspectorMismatch = false;
    this.allInspectors = (await this.inspectorService
      .inspectorsWithMiles({
        search: '',
        distanceTo: [this.inspection.location[0], this.inspection.location[1]].toString(),
      })
      .toPromise()) || [{}];

    const inspectionProductTypeName = this.inspectionProductTypeName;

    if (!inspectionProductTypeName) {
      console.error('Inspection Product Type Name is not defined.');
      return;
    }
    this.vendorInspectors = this.allInspectors.filter(inspector => {
      if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
        return false;
      }
      const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
      return productTypesArray.includes(inspectionProductTypeName);
    });
    this.inspectorDataSource = this.vendorInspectors;
  }
}
